/**
 * R3F component rendering impact flashes when tracers hit entities.
 * Reads impact state from ProjectileSystem module.
 * InstancedMesh pool of expanding, fading circles.
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import * as projectileSystem from './ProjectileSystem';
import { impactVertexShader, impactFragmentShader } from '../shaders/tracerShaders';

const MAX_IMPACT_INSTANCES = 20;
const IMPACT_MAX_SCALE = 4.0;

// Reusable objects
const tempMatrix = new THREE.Matrix4();
const tempPosition = new THREE.Vector3();
const tempQuaternion = new THREE.Quaternion();
const tempScale = new THREE.Vector3();
const zeroScale = new THREE.Vector3(0, 0, 0);

// Lay flat on XZ plane
const flatRotation = new THREE.Quaternion().setFromEuler(
  new THREE.Euler(-Math.PI / 2, 0, 0),
);

export function ImpactFlash() {
  const meshRef = useRef<THREE.InstancedMesh>(null);

  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        vertexShader: impactVertexShader,
        fragmentShader: impactFragmentShader,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    [],
  );

  const geometry = useMemo(() => new THREE.PlaneGeometry(1, 1), []);

  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;

    const impacts = projectileSystem.getImpacts();
    const lifetime = projectileSystem.getImpactLifetime();

    for (let i = 0; i < MAX_IMPACT_INSTANCES && i < impacts.length; i++) {
      const impact = impacts[i];

      if (impact.age >= lifetime) {
        tempMatrix.compose(tempPosition, tempQuaternion, zeroScale);
        mesh.setMatrixAt(i, tempMatrix);
        continue;
      }

      const progress = impact.age / lifetime; // 0 → 1
      const scale = IMPACT_MAX_SCALE * progress;
      const opacity = 1.0 - progress;

      tempPosition.set(impact.x, 0.6, impact.y);
      tempScale.set(scale * opacity, scale * opacity, scale * opacity);
      tempMatrix.compose(tempPosition, flatRotation, tempScale);
      mesh.setMatrixAt(i, tempMatrix);
    }

    mesh.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, MAX_IMPACT_INSTANCES]}
      frustumCulled={false}
    />
  );
}
